import { Platform, DeviceInfo } from '../types';
import { generateUUID } from '../../../utils/uuid';
import { BrowserPlatformService } from './BrowserPlatformService';
import { parseAndroidUserAgent } from '../utils/androidUserAgent';

/**
 * EmbeddedPlatformService — Efflux rodando EMBEDDED dentro de outro APK Android
 * (o "host") como um WebView controlado por uma biblioteca (AAR).
 *
 * O ambiente de runtime é um WebView (Chromium no Android), então herda quase
 * tudo do {@link BrowserPlatformService} e sobrescreve só o que é específico do
 * modo embedded:
 * - `platform` = 'embedded' (distingue nos logs/telemetria/sync).
 * - `isNativeEnvironment()` = true (roda dentro de um app nativo host, controlado).
 * - `getDeviceInfo()` reporta `platform: 'embedded'`.
 * - `getHardwareId()` usa uma chave/prefixo próprios (não colide com o id de browser).
 *
 * Fase 1 (este arquivo): apenas o alvo/plataforma. O canal de comando/evento com
 * o host (start/stop/hide/bounds), a identidade vinda do host e o cache nativo
 * entram nas fases seguintes — vide FEATURE-EMBEDDED-ANDROID.md. NADA aqui altera
 * o comportamento dos outros alvos: só é instanciado quando `platform==='embedded'`.
 */
export class EmbeddedPlatformService extends BrowserPlatformService {
  readonly platform: Platform = 'embedded';

  init(): void {
    console.log(
      '[EmbeddedPlatformService] Initializing (host-embedded WebView)...',
    );
    // Reusa o setup do Browser (orientation listener etc.).
    super.init();
  }

  /**
   * Embedded roda dentro de um app nativo host que controla o ciclo de vida —
   * é um ambiente "nativo" (diferente de um browser solto).
   */
  isNativeEnvironment(): boolean {
    return true;
  }

  /**
   * RAM livre do aparelho pelo bridge do host.
   *
   * <p>O embedded roda numa WebView: não há API de sistema no JS. Quem tem
   * `ActivityManager` é o APK do parceiro, e o AAR já expõe um canal
   * (`EffluxHost`) — o método entra ali.</p>
   *
   * <p>Chamado com otimismo: AAR que ainda não publica `memoryInfo` devolve
   * null e a série segue sem o campo. Quando o parceiro atualizar, passa a
   * reportar sem precisar de OTA nova do bundle.</p>
   */
  async getSystemMemoryMb(): Promise<{
    freeMb: number;
    totalMb: number | null;
  } | null> {
    try {
      const host = (
        window as unknown as {
          EffluxHost?: { memoryInfo?: () => string | Promise<string> };
        }
      ).EffluxHost;
      const bruto = await host?.memoryInfo?.();
      if (!bruto) return null;
      const info = JSON.parse(bruto) as { availMb?: number; totalMb?: number };
      const free = Number(info?.availMb);
      if (!Number.isFinite(free) || free <= 0) return null;
      const total = Number(info?.totalMb);
      return {
        freeMb: Math.round(free),
        totalMb: Number.isFinite(total) && total > 0 ? Math.round(total) : null,
      };
    } catch {
      return null;
    }
  }

  /**
   * Info do device: Browser como base, mas com o HARDWARE REAL do tablet.
   *
   * <p>O embedded roda numa WebView dentro do APK do parceiro — o aparelho é um
   * tablet Android igual aos da frota. Herdar o `getDeviceInfo` do Browser sem
   * mais nada fazia o player se anunciar como `model: "Chrome Browser"`,
   * `manufacturer: "Browser"`, `platformVersion: "Linux armv8l"`, e a Tabela de
   * Players mostrava um navegador onde há um SM-X400 rodando Android 16.</p>
   *
   * <p>Duas fontes, nesta ordem:</p>
   *
   * <ol>
   *   <li><b>O host</b>, se injetar os campos em `window.__effluxEmbedded` — é o
   *       dado nativo (`Build.MODEL`), sem ambiguidade. Depende de o parceiro
   *       recompilar com um AAR que os publique.</li>
   *   <li><b>O User-Agent da WebView</b>, que já traz modelo e release hoje. É o
   *       caminho que chega pela OTA do bundle, sem ninguém recompilar nada.</li>
   * </ol>
   *
   * <p>`platformVersion` sai LIMPA (`"16"`, não `"Android 16"`) porque é assim
   * que a frota manda e é o que o admin sabe formatar.</p>
   */
  async getDeviceInfo(): Promise<DeviceInfo> {
    const info = await super.getDeviceInfo();
    const host = this.hardwareDoHost();
    const ua = parseAndroidUserAgent(navigator.userAgent);

    return {
      ...info,
      platform: 'embedded',
      // Só substitui quando há dado melhor: num browser de teste (sem Android no
      // UA e sem host) a resposta do Browser continua valendo.
      model: host.model ?? ua.model ?? info.model,
      manufacturer:
        host.manufacturer ?? (ua.model ? 'Android' : info.manufacturer),
      platformVersion: host.osVersion ?? ua.osVersion ?? info.platformVersion,
    };
  }

  /**
   * Hardware que o host publicou no marcador, se publicou.
   *
   * <p>Contrato futuro: o AAR pode acrescentar `model`, `manufacturer` e
   * `osVersion` ao `window.__effluxEmbedded`. Enquanto não acrescenta, isto
   * devolve nulos e o UA assume — por isso nada aqui é obrigatório.</p>
   */
  private hardwareDoHost(): {
    model: string | null;
    manufacturer: string | null;
    osVersion: string | null;
  } {
    const vazio = { model: null, manufacturer: null, osVersion: null };
    try {
      const m = (window as { __effluxEmbedded?: unknown }).__effluxEmbedded;
      if (!m || typeof m !== 'object') return vazio;
      const texto = (v: unknown): string | null =>
        typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
      const o = m as Record<string, unknown>;
      return {
        model: texto(o.model),
        manufacturer: texto(o.manufacturer),
        osVersion: texto(o.osVersion),
      };
    } catch {
      return vazio;
    }
  }

  /**
   * Id persistente do device no modo embedded.
   *
   * Fase 4b (2026-08-30) — DURABILIDADE: o id é resolvido pelo **host (AAR)** e
   * injetado em `window.__effluxEmbedded.externalId` (ancorado no ANDROID_ID +
   * SharedPreferences → sobrevive a limpar-dados/reinstall do host; só muda em
   * factory reset). O player PREFERE esse id. Fallback (AAR antigo que injeta só
   * `=true`, ou browser de teste): gera/lê do localStorage.
   *
   * Formato: `EMBEDDED-<16 hex, 64 bits>` — mesma convenção do ANDROID_ID da frota
   * → colisão desprezível. (Era 8 hex/32 bits: ~0,4% de colisão em 6k devices.)
   * Enforcement server-side por install-fingerprint foi DESCARTADO (over-engineering)
   * — vide FEATURE-EMBEDDED-ANDROID.md §7.
   */
  async getHardwareId(): Promise<string> {
    // 1. Id durável injetado pelo host (ANDROID_ID). É a fonte de verdade quando presente.
    const marker = (window as { __effluxEmbedded?: unknown }).__effluxEmbedded;
    const hostId =
      marker && typeof marker === 'object'
        ? (marker as { externalId?: unknown }).externalId
        : undefined;
    if (typeof hostId === 'string' && /^EMBEDDED-[0-9A-F]{16}$/.test(hostId)) {
      // Cacheia no localStorage (consistência/telemetria); o host segue autoritativo.
      localStorage.setItem('efflux_embedded_hardware_id', hostId);
      console.log(
        '[EmbeddedPlatformService] Using host-injected embedded ID:',
        hostId,
      );
      return hostId;
    }

    // 2. Fallback: gera/lê do localStorage (AAR antigo sem externalId, ou browser).
    let embeddedId = localStorage.getItem('efflux_embedded_hardware_id');
    if (!embeddedId) {
      embeddedId = `EMBEDDED-${generateUUID().replace(/-/g, '').substring(0, 16).toUpperCase()}`;
      localStorage.setItem('efflux_embedded_hardware_id', embeddedId);
    }
    console.log(
      '[EmbeddedPlatformService] Using embedded ID (fallback):',
      embeddedId,
    );
    return embeddedId;
  }
}
